/**
 * AirdropAddressList — CSV/paste upload component for airdrop recipients.
 *
 * Supports:
 * - CSV file upload (.csv)
 * - Textarea paste (address,amount per line; comma/space/tab separated)
 * - Address validation, deduplication, and supply range validation
 */

import type { AirdropRecipient } from "@repo/shared-types";
import { type ChangeEvent, useCallback, useState } from "react";

import {
	type ParseAddressListResult,
	parseAddressList,
	validateAirdropSupply,
} from "../../lib/airdrop-merkle";

type AirdropAddressListProps = {
	recipients: AirdropRecipient[];
	onRecipientsChange: (recipients: AirdropRecipient[]) => void;
};

export function AirdropAddressList({ recipients, onRecipientsChange }: AirdropAddressListProps) {
	const [textInput, setTextInput] = useState("");
	const [parseResult, setParseResult] = useState<ParseAddressListResult | null>(null);
	const [supplyError, setSupplyError] = useState<string | null>(null);

	const processInput = useCallback(
		(input: string) => {
			if (input.trim().length === 0) {
				setParseResult(null);
				setSupplyError(null);
				onRecipientsChange([]);
				return;
			}

			const result = parseAddressList(input);
			setParseResult(result);

			const totalAmount = result.valid.reduce((sum, r) => sum + r.amount, 0);
			const error = totalAmount > 0 ? validateAirdropSupply(totalAmount) : null;
			setSupplyError(error);

			onRecipientsChange(result.valid);
		},
		[onRecipientsChange]
	);

	const handleTextChange = useCallback(
		(e: ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value;
			setTextInput(value);
			processInput(value);
		},
		[processInput]
	);

	const handleFileUpload = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = (event) => {
				const content = event.target?.result;
				if (typeof content === "string") {
					setTextInput(content);
					processInput(content);
				}
			};
			reader.readAsText(file);
		},
		[processInput]
	);

	const totalAmount = recipients.reduce((sum, r) => sum + r.amount, 0);

	return (
		<div className="space-y-4">
			{/* File Upload */}
			<div>
				<label
					htmlFor="airdrop-csv-upload"
					className="text-sm font-medium text-pastel-black block mb-1.5"
				>
					Upload CSV File
				</label>
				<input
					id="airdrop-csv-upload"
					type="file"
					accept=".csv,.txt"
					onChange={handleFileUpload}
					className="block w-full text-sm text-pastel-gray file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-pastel-vibrant-blue/10 file:text-pastel-vibrant-blue hover:file:bg-pastel-vibrant-blue/20 transition-colors"
				/>
			</div>

			{/* Paste Textarea */}
			<div>
				<label
					htmlFor="airdrop-paste-input"
					className="text-sm font-medium text-pastel-black block mb-1.5"
				>
					Or Paste Address List
				</label>
				<textarea
					id="airdrop-paste-input"
					value={textInput}
					onChange={handleTextChange}
					placeholder={"0x1234...abcd,1000\n0x5678...efgh,2000"}
					rows={6}
					className="w-full rounded-lg border border-pastel-border bg-white px-3 py-2 text-sm text-pastel-black placeholder:text-pastel-gray font-mono focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40 focus:border-pastel-vibrant-blue transition-colors"
				/>
				<p className="text-xs text-pastel-gray mt-1">
					One address per line: address,amount (comma, space, or tab separated)
				</p>
			</div>

			{/* Summary */}
			{recipients.length > 0 && (
				<div className="bg-pastel-light-gray/50 border border-pastel-border rounded-lg p-3">
					<p className="text-sm font-medium text-pastel-black">
						{recipients.length} valid address{recipients.length !== 1 ? "es" : ""},{" "}
						{totalAmount.toLocaleString()} tokens total
					</p>
				</div>
			)}

			{/* Supply Range Error */}
			{supplyError && <p className="text-xs text-red-500">{supplyError}</p>}

			{/* Duplicate Warning */}
			{parseResult && parseResult.duplicatesMerged > 0 && (
				<p className="text-xs text-amber-600">
					{parseResult.duplicatesMerged} duplicate address
					{parseResult.duplicatesMerged !== 1 ? "es" : ""} merged
				</p>
			)}

			{/* Parse Errors */}
			{parseResult && parseResult.errors.length > 0 && (
				<div className="space-y-1">
					<p className="text-xs font-medium text-red-500">Errors:</p>
					<ul className="text-xs text-red-500 space-y-0.5 max-h-32 overflow-y-auto">
						{parseResult.errors.map((error, index) => (
							<li key={`error-${index.toString()}`}>• {error}</li>
						))}
					</ul>
				</div>
			)}

			{/* Address Preview */}
			{recipients.length > 0 && (
				<div className="max-h-48 overflow-y-auto">
					<table className="w-full text-xs">
						<thead>
							<tr className="text-left text-pastel-gray border-b border-pastel-border">
								<th className="pb-1 pr-2">Address</th>
								<th className="pb-1 text-right">Amount</th>
							</tr>
						</thead>
						<tbody>
							{recipients.slice(0, 20).map((recipient) => (
								<tr key={recipient.address} className="border-b border-pastel-border/50">
									<td className="py-1 pr-2 font-mono text-pastel-black">
										{recipient.address.slice(0, 8)}...{recipient.address.slice(-6)}
									</td>
									<td className="py-1 text-right text-pastel-black">
										{recipient.amount.toLocaleString()}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{recipients.length > 20 && (
						<p className="text-xs text-pastel-gray mt-1">
							+ {recipients.length - 20} more addresses
						</p>
					)}
				</div>
			)}
		</div>
	);
}
